import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { Combobox as HeadlessCombobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { MessageBox } from "./MessageBox";
export function Combobox({ items, groups, value, onChange, filterFn, renderItem, displayValue, placeholder = 'Search...', label, errors, disabled = false, className, inputClassName, panelClassName, emptyMessage = 'No results found.' }) {
    const [query, setQuery] = useState('');
    // Build a flat lookup map for all items
    const allItems = useMemo(()=>{
        if (items) return items;
        if (groups) return groups.flatMap((g)=>g.items);
        return [];
    }, [
        items,
        groups
    ]);
    // Default filter: case-insensitive match on displayValue
    const defaultFilter = (item, q)=>{
        if (!displayValue) return true;
        const text = displayValue(item) ?? '';
        return text.toLowerCase().includes(q.toLowerCase());
    };
    const activeFilter = filterFn ?? defaultFilter;
    // Filter items/groups based on query
    const filteredItems = useMemo(()=>{
        if (!query) return items ?? null;
        return items?.filter((item)=>activeFilter(item, query)) ?? null;
    }, [
        items,
        query,
        activeFilter
    ]);
    const filteredGroups = useMemo(()=>{
        if (!query) return groups ?? null;
        return groups?.map((group)=>({
                ...group,
                items: group.items.filter((item)=>activeFilter(item, query))
            })).filter((group)=>group.items.length > 0) ?? null;
    }, [
        groups,
        query,
        activeFilter
    ]);
    const hasResults = filteredItems ? filteredItems.length > 0 : filteredGroups ? filteredGroups.some((g)=>g.items.length > 0) : allItems.length > 0;
    const getDisplayValue = (val)=>{
        if (!val || !displayValue) return '';
        const item = allItems.find((i)=>i.id === val) ?? null;
        return item ? displayValue(item) : '';
    };
    const labelClass = 'block font-bold mb-2 text-sm text-gray-700 mt-0';
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        children: [
            !!label && /*#__PURE__*/ _jsx("label", {
                className: labelClass,
                children: label
            }),
            /*#__PURE__*/ _jsx(HeadlessCombobox, {
                value: value,
                onChange: onChange,
                disabled: disabled,
                onClose: ()=>setQuery(''),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    children: [
                        /*#__PURE__*/ _jsx(Search, {
                            className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 mb-2 h-4 w-4 text-gray-400",
                            "aria-hidden": "true"
                        }),
                        /*#__PURE__*/ _jsx(ComboboxInput, {
                            className: twMerge('bg-navy-5 mb-2 pl-10 pr-10 py-2 text-[15px] w-full mt-0', disabled && 'opacity-50 cursor-not-allowed', inputClassName),
                            displayValue: getDisplayValue,
                            onChange: (e)=>setQuery(e.target.value),
                            placeholder: placeholder
                        }),
                        /*#__PURE__*/ _jsx(ComboboxButton, {
                            className: "absolute inset-y-0 right-0 flex items-center pr-3 mb-2",
                            children: /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                className: "h-4 w-4 text-gray-400",
                                "aria-hidden": "true"
                            })
                        }),
                        /*#__PURE__*/ _jsxs(ComboboxOptions, {
                            anchor: "bottom start",
                            className: twMerge('z-50 max-h-60 w-[var(--input-width)] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none empty:hidden', panelClassName),
                            children: [
                                !hasResults && /*#__PURE__*/ _jsx("div", {
                                    className: "px-4 py-2 text-gray-500",
                                    children: emptyMessage
                                }),
                                filteredItems?.map((item)=>/*#__PURE__*/ _jsxs(ComboboxOption, {
                                        value: item.id,
                                        disabled: item.disabled,
                                        className: "group relative cursor-default select-none py-2 pl-10 pr-4 data-[focus]:bg-gray-100 data-[disabled]:opacity-50",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "absolute inset-y-0 left-0 hidden items-center pl-3 group-data-[selected]:flex text-gray-600",
                                                children: /*#__PURE__*/ _jsx(Check, {
                                                    className: "h-4 w-4",
                                                    "aria-hidden": "true"
                                                })
                                            }),
                                            renderItem(item)
                                        ]
                                    }, item.id)),
                                filteredGroups?.map((group, groupIdx)=>/*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            groupIdx > 0 && /*#__PURE__*/ _jsx("div", {
                                                className: "border-t border-gray-200 my-1"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider",
                                                children: group.label
                                            }),
                                            group.items.map((item)=>/*#__PURE__*/ _jsxs(ComboboxOption, {
                                                    value: item.id,
                                                    disabled: item.disabled,
                                                    className: "group relative cursor-default select-none py-2 pl-10 pr-4 data-[focus]:bg-gray-100 data-[disabled]:opacity-50",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "absolute inset-y-0 left-0 hidden items-center pl-3 group-data-[selected]:flex text-gray-600",
                                                            children: /*#__PURE__*/ _jsx(Check, {
                                                                className: "h-4 w-4",
                                                                "aria-hidden": "true"
                                                            })
                                                        }),
                                                        renderItem(item)
                                                    ]
                                                }, item.id))
                                        ]
                                    }, group.label))
                            ]
                        })
                    ]
                })
            }),
            !!errors?.message && /*#__PURE__*/ _jsx(MessageBox, {
                className: "px-4 py-2 mb-6",
                message: errors.message,
                type: "error"
            })
        ]
    });
}
